// External Intake & Collection v1.0 PR 2 — AI drafting modal for the
// template-builder's two prompts. Mirrors the Reports PromptBuilderModal
// UX (description → generate → refine → accept) but kept lean: the
// Collection flow has no form-schema/category context to surface, just
// the template kind + name + which of the two prompts is being drafted.

import { useEffect, useRef, useState } from "react";
import { AlertTriangle, Sparkles } from "lucide-react";
import { Modal } from "@/components/ui/Modal";
import { Textarea } from "@/components/ui/Select";
import { Button } from "@/design-system/components/ui";
import { useToast } from "@/components/ui/Toast";
import { ApiError } from "@/api/client";
import {
  draftCollectionPrompt,
  CollectionPromptQuotaError,
  type CollectionPromptKind,
  type DraftCollectionPromptRequest,
  type DraftCollectionPromptResult,
  type DraftCollectionPromptTurn,
} from "@/api/collectionTemplates";

const MIN_DESCRIPTION_CHARS = 20;

interface CollectionPromptDraftingModalProps {
  templateId: string;
  templateName: string;
  kind: CollectionPromptKind;
  onAcceptDraft: (body: string) => void;
  onClose: () => void;
}

type ModalState = "empty" | "generating" | "result";

export function CollectionPromptDraftingModal({
  templateId,
  templateName,
  kind,
  onAcceptDraft,
  onClose,
}: CollectionPromptDraftingModalProps) {
  const { toast } = useToast();
  const [modalState, setModalState] = useState<ModalState>("empty");
  const [description, setDescription] = useState("");
  const [refinementHistory, setRefinementHistory] = useState<DraftCollectionPromptTurn[]>([]);
  const [refineFeedback, setRefineFeedback] = useState("");
  const [lastResult, setLastResult] = useState<DraftCollectionPromptResult | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [quotaBanner, setQuotaBanner] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => () => abortRef.current?.abort(), []);

  const generateDisabled =
    description.trim().length < MIN_DESCRIPTION_CHARS || modalState === "generating";
  const refineDisabled = refineFeedback.trim().length === 0 || modalState === "generating";

  async function callBackend(history: DraftCollectionPromptTurn[] | null) {
    setErrorMessage(null);
    abortRef.current?.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    setModalState("generating");

    const body: DraftCollectionPromptRequest = {
      kind,
      description: description.trim(),
      refinementHistory: history && history.length > 0 ? history : null,
    };
    try {
      const result = await draftCollectionPrompt(templateId, body, ctrl.signal);
      if (ctrl.signal.aborted) return;
      setLastResult(result);
      setQuotaBanner(null);
      setModalState("result");
    } catch (err) {
      if (err instanceof DOMException && err.name === "AbortError") return;
      handleApiError(err);
    }
  }

  function handleApiError(err: unknown) {
    if (err instanceof CollectionPromptQuotaError) {
      const minutes =
        err.retryAfterSeconds !== null ? Math.max(1, Math.round(err.retryAfterSeconds / 60)) : null;
      const msg = minutes
        ? `Daily AI draft limit reached. Resets in ~${minutes} minute${minutes === 1 ? "" : "s"} (UTC midnight).`
        : "Daily AI draft limit reached. Resets at midnight UTC.";
      setQuotaBanner(msg);
      setModalState(lastResult ? "result" : "empty");
      if (!lastResult) setErrorMessage(msg);
      return;
    }
    if (err instanceof ApiError) {
      if (err.status === 403) {
        toast({ tone: "error", message: "You don't have permission to use this feature." });
        window.setTimeout(() => onClose(), 3000);
        return;
      }
      if (err.status >= 500) {
        setErrorMessage(
          "The drafting service is temporarily unavailable. Try again, or write the prompt manually.",
        );
        setModalState(lastResult ? "result" : "empty");
        return;
      }
      setErrorMessage(err.errors[0] ?? err.message ?? "Couldn't draft that prompt.");
      setModalState(lastResult ? "result" : "empty");
      return;
    }
    setErrorMessage(err instanceof Error ? err.message : "Unexpected error.");
    setModalState(lastResult ? "result" : "empty");
  }

  function handleGenerate() {
    if (generateDisabled) return;
    setRefinementHistory([]);
    setLastResult(null);
    setQuotaBanner(null);
    void callBackend(null);
  }

  function handleRefine() {
    if (refineDisabled || !lastResult) return;
    const turn: DraftCollectionPromptTurn = {
      adminFeedback: refineFeedback.trim(),
      assistantDraft: lastResult.draftedPromptBody,
    };
    const next = [...refinementHistory, turn];
    setRefinementHistory(next);
    setRefineFeedback("");
    void callBackend(next);
  }

  function handleAccept() {
    if (!lastResult) return;
    onAcceptDraft(lastResult.draftedPromptBody);
    onClose();
  }

  return (
    <Modal
      open
      onClose={onClose}
      dismissOnBackdropClick={false}
      size="lg"
      title={`Draft ${kind === "Feedback" ? "feedback" : "assessment"} prompt`}
      description={`AI-assisted draft for "${templateName}" — describe what you want this prompt to do.`}
      footer={
        <>
          <Button variant="secondary" type="button" onClick={onClose}>
            Cancel
          </Button>
          {modalState === "result" && lastResult && (
            <Button type="button" onClick={handleAccept}>
              Use this draft
            </Button>
          )}
        </>
      }
    >
      <div className="flex flex-col gap-space-4">
        {quotaBanner && (
          <div className="flex items-start gap-space-2 rounded-md bg-status-warning/10 p-space-3 text-small text-onyx">
            <AlertTriangle size={16} className="mt-[2px] text-status-warning" />
            <span>{quotaBanner}</span>
          </div>
        )}

        <label className="flex flex-col gap-space-2 text-small text-onyx">
          <span className="font-medium">
            Describe what this {kind === "Feedback" ? "feedback" : "assessment"} prompt should do
          </span>
          <Textarea
            rows={5}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder={
              kind === "Feedback"
                ? "e.g. Check the uploaded tax return is signed, all pages are present, and that the SSN/EIN matches the borrower's expected entity. Be friendly and tell them what to fix."
                : "e.g. Pull out the borrower's full name, SSN, taxable income, AGI, and any flags (amended returns, missing schedules) into a one-screen summary for the loan administrator."
            }
          />
          <span className="text-caption text-gray-mid">
            {description.trim().length} / min {MIN_DESCRIPTION_CHARS} chars
          </span>
        </label>

        {modalState === "generating" && (
          <div className="flex items-center gap-space-2 text-small text-gray-mid">
            <Sparkles size={16} className="animate-pulse text-gold" />
            <span>Drafting via Claude…</span>
          </div>
        )}

        {errorMessage && (
          <div className="rounded-md bg-status-error/10 p-space-3 text-small text-onyx">
            {errorMessage}
          </div>
        )}

        {modalState === "result" && lastResult && (
          <div className="flex flex-col gap-space-3">
            <div className="flex items-center justify-between text-caption text-gray-mid">
              <span>
                Drafts used today: {lastResult.draftsUsedToday} / {lastResult.dailyDraftLimit}
              </span>
              <span>
                {lastResult.inputTokens} in / {lastResult.outputTokens} out · $
                {lastResult.estimatedCostUsd.toFixed(4)}
              </span>
            </div>
            {lastResult.warnings.length > 0 && (
              <div className="rounded-md bg-status-warning/10 p-space-3 text-small text-onyx">
                <ul className="m-0 pl-space-4">
                  {lastResult.warnings.map((w, i) => (
                    <li key={i}>{w}</li>
                  ))}
                </ul>
              </div>
            )}
            <Textarea
              rows={10}
              readOnly
              value={lastResult.draftedPromptBody}
              aria-label="Drafted prompt body"
            />
            <label className="flex flex-col gap-space-2 text-small text-onyx">
              <span className="font-medium">Refine (optional)</span>
              <Textarea
                rows={2}
                value={refineFeedback}
                onChange={(e) => setRefineFeedback(e.target.value)}
                placeholder="e.g. shorter, more direct; ask about Schedule C explicitly"
              />
              <Button
                variant="secondary"
                size="sm"
                type="button"
                disabled={refineDisabled}
                onClick={handleRefine}
                className="self-start"
              >
                Refine
              </Button>
            </label>
          </div>
        )}

        {modalState !== "generating" && !lastResult && (
          <Button type="button" disabled={generateDisabled} onClick={handleGenerate}>
            Generate draft
          </Button>
        )}
      </div>
    </Modal>
  );
}
